// Stock counting on the phone (mockup counting-v1 §2, owner 2026-10-28/30): pick a location from
// an open round or scan the one in front of you; the location is the big thing and under it what
// Trail believes stands there, one row per container; each scan ticks a row, a pallet scan ticks
// its cartons; a container Trail had elsewhere is "hittad här" (moved here with reason, or left);
// "Platsen klar" asks about rows left and sends one Counted observation — missing containers are
// not corrections; the lead turns them into adjustments on the desktop.

import { COUNT, classOf, identityKey, parseScan, uuidv7, type EventEnvelope, type GS1Elements } from "@trail/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Api, effectiveSettings as getSettings, type ContainerSummary, type CountLocationInfo, type CountRound, type CountRoundLocation, type StepBatch } from "../api/client.js";
import { fmtQty, t } from "../i18n.js";
import { pending, startQueueWorker, submitStep, subscribe, type QueuedStep } from "../receive/queue.js";
import { missingLocationMessage } from "../receive/location-message.js";
import { buildRegistry, cacheBundle, findLocation, loadCachedBundle, type Registry } from "../receive/registry.js";
import { isGs1Symbology } from "../scan/camera.js";
import { feedback, unlockAudio } from "../scan/feedback.js";
import { attachWedge } from "../scan/wedge.js";
import { Actions, Banner, Btn, Entry, ListItem, Prompt, Rows, ScanBar, Sheet, Strip, focusScan, type BannerData, type RowData } from "../ui/components.jsx";

const FLOW = COUNT;

type Phase =
  | { kind: "pick" }
  | { kind: "count"; info: CountLocationInfo; roundId: string | null }
  | { kind: "missing"; info: CountLocationInfo; roundId: string | null }
  | { kind: "done"; code: string; present: number; found: number; missing: number; queued: boolean };

interface Found { container: ContainerSummary; fromLocationId: string | null }

export function Count({ onClose }: { onClose: () => void }) {
  const settings = useMemo(getSettings, []);
  const [registry, setRegistry] = useState<Registry | null>(() => { const c = loadCachedBundle(); return c ? buildRegistry(c) : null; });
  const [queue, setQueue] = useState<QueuedStep[]>(pending);
  const [online, setOnline] = useState(navigator.onLine);
  const [rounds, setRounds] = useState<CountRound[] | null>(null);
  const [roundLocs, setRoundLocs] = useState<{ round: CountRound; locations: CountRoundLocation[] } | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "pick" });
  const [banner, setBanner] = useState<BannerData | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    startQueueWorker();
    Api.bundle().then((b) => { cacheBundle(b); setRegistry(buildRegistry(b)); }).catch(() => undefined);
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    const unsub = subscribe(setQueue);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); unsub(); };
  }, []);
  useEffect(() => {
    if (phase.kind === "pick") Api.countRounds("open").then(setRounds).catch(() => setRounds([]));
  }, [phase.kind]);

  const net = { online, queued: queue.length, stuck: queue.filter((q) => q.lastError && !/fetch|network|abort|5\d\d|401|429/i.test(q.lastError)).length };
  const counting = registry?.bundle.siteConfig?.counting;
  const adHoc = counting?.adHoc ?? true;

  /** Open a location: claim it, read what Trail expects there. */
  const open = useCallback(async (locationId: string, roundId: string | null) => {
    setBanner(null);
    try {
      await Api.claimCountLocation(locationId, roundId);
      const info = await Api.countLocation(locationId);
      feedback("accepted");
      setPhase({ kind: "count", info, roundId: roundId ?? info.rounds[0]?.id ?? null });
    } catch (e) {
      feedback("rejected");
      const msg = e instanceof Error ? e.message : String(e);
      const who = /counted by (.+)$/.exec(msg)?.[1];
      setBanner({ kind: "bad", label: t("count.locked"), message: who ? t("count.lockedBy", { who }) : msg });
    }
  }, []);

  // Scanning a location code in the pick phase opens it (ad hoc when the site allows, else only from a round).
  useEffect(() => {
    if (phase.kind !== "pick") return;
    const el = inputRef.current;
    if (!el || !registry) return;
    focusScan(el);
    return attachWedge(el, {
      onScan: (d) => {
        const text = d.replace(/^\]\w\w/, "").trim();
        const loc = findLocation(registry, text);
        if (!loc) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: missingLocationMessage(registry, text) ?? t("count.notLocation", { code: text.slice(0, 40) }) }); return; }
        const inRound = roundLocs?.locations.find((l) => l.locationId === loc.id);
        if (!inRound && !adHoc) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("count.noAdHoc") }); return; }
        void open(loc.id, inRound && roundLocs ? roundLocs.round.id : null);
      },
    });
  }, [phase.kind, registry, roundLocs, adHoc, open]);

  if (!registry) {
    return (
      <div className="screen">
        <Strip left={<b>{t("menu.count")}</b>} {...net} />
        <div className="body"><p className="hint">{t("common.loading")}</p></div>
        <Actions><Btn kind="quiet" icon="back" onClick={onClose}>{t("common.menu")}</Btn></Actions>
      </div>
    );
  }

  if (phase.kind === "count" || phase.kind === "missing") {
    return (
      <CountLocation
        key={phase.info.location.id}
        registry={registry}
        settings={settings}
        info={phase.info}
        roundId={phase.roundId}
        askMissing={phase.kind === "missing"}
        net={net}
        quantityRule={counting?.quantity ?? "split"}
        onMissing={() => setPhase({ ...phase, kind: "missing" })}
        onBackToCount={() => setPhase({ ...phase, kind: "count" })}
        onDone={(d) => setPhase({ kind: "done", ...d })}
        onAbort={() => { Api.releaseCountLocation(phase.info.location.id).catch(() => undefined); setPhase({ kind: "pick" }); }}
      />
    );
  }

  if (phase.kind === "done") {
    return (
      <div className="screen">
        <Strip left={<b>{t("menu.count")}</b>} middle={phase.code} {...net} />
        <div className="body">
          <Banner kind={phase.missing > 0 ? "warn" : "ok"} label={t("count.doneTitle")} message={t("count.doneMsg", { present: phase.present, found: phase.found, missing: phase.missing })} />
          {phase.missing > 0 && <p className="hint">{t("count.doneReview")}</p>}
          {phase.queued && <p className="hint">{t("count.queued")}</p>}
        </div>
        <Actions>
          <Btn kind="quiet" icon="back" onClick={onClose}>{t("common.menu")}</Btn>
          <Btn kind="primary" onClick={() => setPhase({ kind: "pick" })}>{t("count.nextLocation")}</Btn>
        </Actions>
      </div>
    );
  }

  // Pick phase: rounds with their pending locations, or scan the location in front of you.
  return (
    <div className="screen" onClick={() => { unlockAudio(); focusScan(inputRef.current); }}>
      <Strip left={<b>{t("menu.count")}</b>} middle={roundLocs ? roundLocs.round.name : undefined} {...net} />
      <div className="body">
        <Prompt step={t("count.step1")} title={roundLocs ? t("count.pickLocation") : t("count.pickRound")} sub={adHoc ? t("count.sub1") : t("count.sub1.roundOnly")} />
        <ScanBar inputRef={inputRef} placeholder={t("count.scanLocation")} />
        {banner && <Banner {...banner} />}
        {!roundLocs && (
          <div className="list">
            {rounds === null && <p className="hint">{t("common.loading")}</p>}
            {rounds?.length === 0 && <p className="hint">{t("count.noRounds")}</p>}
            {rounds?.map((r) => (
              <ListItem key={r.id} icon="bin" title={r.name} desc={t("count.roundDesc", { done: r.counted, total: r.locations, counting: r.counting })} onClick={() => void Api.countRound(r.id).then((d) => setRoundLocs({ round: d, locations: d.locationList })).catch(() => undefined)} />
            ))}
          </div>
        )}
        {roundLocs && (
          <div className="list">
            {roundLocs.locations.filter((l) => l.state !== "counted").length === 0 && <p className="hint">{t("count.roundAllCounted")}</p>}
            {roundLocs.locations.filter((l) => l.state !== "counted").map((l) => (
              <ListItem key={l.locationId} icon="bin" title={l.code} desc={l.state === "counting" ? t("count.locBusy", { who: l.heldBy ?? "?" }) : t("count.locPending")} off={l.state === "counting" && l.heldBy !== settings.actorId} onClick={() => void open(l.locationId, roundLocs.round.id)} />
            ))}
          </div>
        )}
      </div>
      <Actions>
        <Btn kind="quiet" icon="back" onClick={() => (roundLocs ? setRoundLocs(null) : onClose())}>{roundLocs ? t("count.allRounds") : t("common.menu")}</Btn>
      </Actions>
    </div>
  );
}

interface LocProps {
  registry: Registry;
  settings: ReturnType<typeof getSettings>;
  info: CountLocationInfo;
  roundId: string | null;
  askMissing: boolean;
  net: { online: boolean; queued: number; stuck: number };
  quantityRule: "none" | "split" | "all";
  onMissing: () => void;
  onBackToCount: () => void;
  onDone: (d: { code: string; present: number; found: number; missing: number; queued: boolean }) => void;
  onAbort: () => void;
}

function CountLocation({ registry, settings, info, roundId, askMissing, net, quantityRule, onMissing, onBackToCount, onDone, onAbort }: LocProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [found, setFound] = useState<Found[]>([]);
  const [quantities, setQuantities] = useState<Map<string, string>>(new Map());
  const [askQty, setAskQty] = useState<ContainerSummary | null>(null);
  const [qty, setQty] = useState("");
  const [hit, setHit] = useState<{ container: ContainerSummary; elements: GS1Elements } | null>(null);
  /** Scanned here, unknown to Trail altogether: offered to the office as a deviation (counting step 3). */
  const [unknown, setUnknown] = useState<{ key: string; elements: GS1Elements } | null>(null);
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [busy, setBusy] = useState(false);
  const qtyRef = useRef<HTMLInputElement>(null);

  const all = useMemo(() => [...info.containers, ...found.map((f) => f.container)], [info.containers, found]);
  const roots = useMemo(() => all.filter((c) => !c.parent_id || !all.some((p) => p.container_id === c.parent_id)), [all]);
  const kidsOf = useMemo(() => { const m = new Map<string, ContainerSummary[]>(); for (const c of all) if (c.parent_id) m.set(c.parent_id, [...(m.get(c.parent_id) ?? []), c]); return m; }, [all]);
  const byKey = useMemo(() => new Map(all.filter((c) => c.identity_key).map((c) => [c.identity_key as string, c])), [all]);
  const isTicked = useCallback((c: ContainerSummary) => ticked.has(c.container_id) || (!!c.parent_id && ticked.has(c.parent_id)), [ticked]);
  const missing = useMemo(() => all.filter((c) => !isTicked(c) && !(c.kind === "logistic-unit" && (kidsOf.get(c.container_id) ?? []).length > 0 && (kidsOf.get(c.container_id) ?? []).every(isTicked))), [all, isTicked, kidsOf]);
  const present = useMemo(() => all.filter(isTicked).length, [all, isTicked]);

  const productOf = useCallback((c: ContainerSummary) => (c.product_id ? registry.bundle.products.find((p) => p.id === c.product_id) : undefined) ?? (c.gtin ? registry.byGtin.get(c.gtin)?.product : undefined), [registry]);
  const describe = useCallback((c: ContainerSummary) => `${c.gtin ? (productOf(c)?.sku ?? c.gtin) : c.sscc ? `SSCC ${c.sscc.slice(-6)}` : "?"}${c.lot_number && c.kind !== "loose" ? ` · ${c.lot_number}` : ""}${c.serial ? ` · ${c.serial}` : ""}`, [productOf]);

  const tick = useCallback((c: ContainerSummary) => {
    setTicked((s) => new Set([...s, c.container_id]));
    feedback("accepted");
    // Quantity per site rule: every container, or only one that has been split (not intact).
    // A bulk row (owner 2026-11-03: no identity) is always counted by its quantity.
    if (c.kind !== "logistic-unit" && (quantityRule === "all" || c.kind === "loose" || (quantityRule === "split" && !c.intact))) { setAskQty(c); setQty(c.kind === "loose" ? "" : String(Number(c.quantity))); }
  }, [quantityRule]);

  const onScan = useCallback(async (d: string) => {
    if (askQty || hit || unknown) return;
    const aim = /^\]\w\w/.exec(d)?.[0] ?? null;
    let elements: GS1Elements | null = null;
    if (!(aim && !isGs1Symbology(d))) { try { elements = await parseScan(d); } catch { elements = null; } }
    const key = elements ? (elements.sscc ? `sscc:00=${elements.sscc}` : identityKey("gtin+lot", elements)) : null;
    if (!key || !elements) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("count.notContainer") }); return; }
    const row = byKey.get(key);
    if (row) { if (isTicked(row)) { feedback("accepted"); setBanner({ kind: "ok", label: t("count.already"), message: describe(row) }); } else { setBanner(null); tick(row); } return; }
    // Not on the list: does Trail have it somewhere else?
    try {
      const rows = await Api.containersByIdentity(key, 4000);
      const c = rows.find((x) => ["received", "stored"].includes(x.state ?? "")) ?? rows[0] ?? null;
      if (!c) { feedback("rejected"); setUnknown({ key, elements }); return; }
      feedback("accepted");
      setHit({ container: c, elements });
    } catch (e) {
      feedback("rejected");
      setBanner({ kind: "bad", label: t("banner.rejected"), message: e instanceof Error ? e.message : String(e) });
    }
  }, [askQty, hit, unknown, byKey, isTicked, tick, describe]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el || askMissing || askQty || hit || unknown) return;
    focusScan(el);
    return attachWedge(el, { onScan: (d) => void onScan(d) });
  }, [onScan, askMissing, askQty, hit, unknown]);
  useEffect(() => { if (askQty) setTimeout(() => qtyRef.current?.focus(), 50); }, [askQty]);

  const finish = async () => {
    setBusy(true);
    const batchId = uuidv7();
    const now = new Date().toISOString();
    const base = (type: EventEnvelope["type"], payload: Record<string, unknown>): EventEnvelope => ({
      eventId: uuidv7(), tenantId: settings.tenantId, goodsOwnerId: registry.bundle.goodsOwner.id, type, class: classOf(type), schemaVersion: 1, occurredAt: now,
      actorId: settings.actorId, deviceId: settings.deviceId, capture: "barcode", captureDetail: null, readPoint: info.location.id, bizLocation: info.location.id, flowId: FLOW.id, flowVersion: FLOW.version, batchId, payload,
    });
    const events: EventEnvelope[] = [];
    for (const f of found) events.push(base("moved", { containerIds: [f.container.container_id], fromLocationId: f.fromLocationId, toLocationId: info.location.id, proposedLocationId: null, overrideReason: t("count.movedReason") }));
    const tickedRoots = roots.filter(isTicked).map((c) => c.container_id);
    const tickedKids = all.filter((c) => c.parent_id && ticked.has(c.container_id) && !ticked.has(c.parent_id)).map((c) => c.container_id);
    events.push(base("counted", {
      locationId: info.location.id,
      containerIds: [...tickedRoots, ...tickedKids],
      missingContainerIds: missing.map((c) => c.container_id),
      quantities: [...quantities].map(([containerId, value]) => ({ containerId, value, uom: all.find((c) => c.container_id === containerId)?.uom ?? "EA" })),
      roundId,
      empty: all.length === 0,
    }));
    const batch: StepBatch = { flowId: FLOW.id, flowVersion: FLOW.version, stepId: "items", events, effects: [] };
    let result: "stored" | "queued" = "queued";
    try { result = await submitStep(batch); } catch { result = "queued"; }
    setBusy(false);
    onDone({ code: info.location.code, present, found: found.length, missing: missing.length, queued: result === "queued" });
  };

  /** An Inspected observation (disposition unknown) at this location, for the lead's deviation list. */
  const reportUnknown = async (key: string, elements: GS1Elements) => {
    setUnknown(null);
    const ev: EventEnvelope = {
      eventId: uuidv7(), tenantId: settings.tenantId, goodsOwnerId: registry.bundle.goodsOwner.id, type: "inspected", class: classOf("inspected"), schemaVersion: 1, occurredAt: new Date().toISOString(),
      actorId: settings.actorId, deviceId: settings.deviceId, capture: "barcode", captureDetail: null, readPoint: info.location.id, bizLocation: info.location.id, flowId: FLOW.id, flowVersion: FLOW.version, batchId: uuidv7(),
      payload: { containerId: null, disposition: "unknown", detail: "unknown-container", expected: null, observed: key, reason: t("count.unknownReason", { code: info.location.code }), gtin: elements.gtin ?? null, lot: elements.lot ?? null, expectedReceiptId: null, action: "recorded" },
    };
    try { await submitStep({ flowId: FLOW.id, flowVersion: FLOW.version, stepId: "items", events: [ev], effects: [] }); } catch { /* queued */ }
    feedback("accepted");
    setBanner({ kind: "warn", label: t("count.unknown"), message: t("count.reported", { key: key.slice(0, 60) }) });
  };

  const rows: RowData[] = roots.flatMap((c) => {
    const kids = kidsOf.get(c.container_id) ?? [];
    const head: RowData = { k: productOf(c)?.name ?? (c.kind === "logistic-unit" ? t("unit.pallet") : describe(c)), small: `${describe(c)}${quantities.has(c.container_id) ? ` · ${quantities.get(c.container_id)} ${c.uom}` : ""}`, q: isTicked(c) ? "✓" : "–", tone: isTicked(c) ? "ok" : undefined, level: kids.length ? "head" : undefined };
    return [head, ...kids.map((k): RowData => ({ k: productOf(k)?.name ?? describe(k), small: describe(k), q: isTicked(k) ? "✓" : "–", tone: isTicked(k) ? "ok" : undefined, level: "sub" }))];
  });

  if (askMissing) {
    return (
      <div className="screen">
        <Strip left={<b>{t("menu.count")}</b>} middle={info.location.code} {...net} />
        <div className="body">
          <Prompt step={t("count.step3")} title={missing.length === 1 ? t("count.missingOne") : t("count.missingN", { n: missing.length })} sub={t("count.missingSub", { code: info.location.code })} />
          <Rows rows={missing.map((c) => ({ k: productOf(c)?.name ?? describe(c), small: describe(c), q: t("count.missingTag"), tone: "warn" as const }))} />
          <p className="hint">{t("count.missingHint")}</p>
        </div>
        <Actions>
          <Btn kind="quiet" icon="back" onClick={onBackToCount}>{t("count.forgotScan")}</Btn>
          <Btn kind="primary" disabled={busy} onClick={() => void finish()}>{t("count.reportMissing")}</Btn>
        </Actions>
      </div>
    );
  }

  return (
    <div className="screen" onClick={() => { unlockAudio(); focusScan(inputRef.current); }}>
      <Strip left={<b>{t("menu.count")}</b>} middle={info.rounds[0]?.name} {...net} />
      <div className="body">
        <Prompt step={t("count.step2")} title={t("count.scanAll")} sub={`${info.location.code} · ${t("count.expected", { n: all.length })}`} />
        <ScanBar inputRef={inputRef} placeholder={t("count.scanContainer")} />
        {banner && <Banner {...banner} />}
        {all.length === 0 && <p className="hint">{t("count.nothingExpected")}</p>}
        <Rows rows={rows} />
        <p className="hint">{t("count.progress", { done: present, total: all.length })}</p>
      </div>
      {hit && (
        <Sheet title={t("count.foundHere")} onClose={() => setHit(null)}>
          <p>{t("count.foundHereMsg", { what: describe(hit.container), where: registry.bundle.locations.find((l) => l.id === hit.container.location_id)?.code ?? "?" })}</p>
          <div className="chips">
            <button type="button" className="chip on" onClick={() => { setFound((f) => [...f, { container: hit.container, fromLocationId: hit.container.location_id }]); setTicked((s) => new Set([...s, hit.container.container_id])); setHit(null); setBanner({ kind: "ok", label: t("count.movedHere"), message: describe(hit.container) }); }}>{t("count.moveHere")}</button>
            <button type="button" className="chip" onClick={() => { setHit(null); setBanner({ kind: "warn", label: t("count.leftThere"), message: describe(hit.container) }); }}>{t("count.leaveThere")}</button>
            <button type="button" className="chip" onClick={() => { setHit(null); setBanner({ kind: "warn", label: t("count.wrongItem"), message: t("count.wrongItemMsg") }); }}>{t("count.wrongItem")}</button>
          </div>
        </Sheet>
      )}
      {unknown && (
        <Sheet title={t("count.unknown")} onClose={() => setUnknown(null)}>
          <p>{t("count.unknownMsg", { key: unknown.key.slice(0, 60) })}</p>
          <div className="chips">
            <button type="button" className="chip on" onClick={() => void reportUnknown(unknown.key, unknown.elements)}>{t("count.reportUnknown")}</button>
            <button type="button" className="chip" onClick={() => setUnknown(null)}>{t("count.skipUnknown")}</button>
          </div>
        </Sheet>
      )}
      {askQty && (
        <Sheet title={t("count.qtyTitle")} onClose={() => setAskQty(null)}>
          <p>{describe(askQty)} · {t("count.qtyNow", { q: fmtQty(askQty.quantity, askQty.uom) })}</p>
          <Entry inputRef={qtyRef} value={qty} onChange={setQty} inputMode="numeric" placeholder={askQty.uom} onEnter={() => { if (qty !== "" && Number(qty) >= 0) { setQuantities((m) => new Map(m).set(askQty.container_id, qty)); setAskQty(null); } }} />
          <div className="chips">
            <button type="button" className="chip on" onClick={() => { if (qty !== "") setQuantities((m) => new Map(m).set(askQty.container_id, qty)); setAskQty(null); }}>{t("common.confirm")}</button>
            <button type="button" className="chip" onClick={() => setAskQty(null)}>{t("count.qtySkip")}</button>
          </div>
        </Sheet>
      )}
      <Actions>
        <Btn kind="quiet" icon="back" onClick={onAbort}>{t("common.cancel")}</Btn>
        <Btn kind="primary" disabled={busy} onClick={() => (missing.length > 0 ? onMissing() : void finish())}>{t("count.locationDone")}</Btn>
      </Actions>
    </div>
  );
}
